import React, { useState, useEffect } from 'react';
import { 
  ShoppingCart, 
  Search, 
  Filter, 
  CheckCircle, 
  XCircle, 
  Clock,
  Package,
  Eye,
  Download,
  AlertTriangle,
  Truck,
  CheckCircle2,
  X,
  ArrowRight,
  Bell,
  Calendar,
  User,
  MapPin,
  CreditCard,
  FileText,
  RefreshCw
} from 'lucide-react';
import './OrderManagement.css';

const OrderManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    status: '',
    payment_status: '',
    date_from: '',
    date_to: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_orders: 0,
    per_page: 10
  });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusReason, setStatusReason] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({});

  // Fetch orders with filters and pagination
  const fetchOrders = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const vendorData = JSON.parse(localStorage.getItem('vendorData') || '{}');
      
      if (!token && !vendorData.id) {
        console.error('No authentication token or vendor data found');
        return;
      }

      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.per_page.toString(),
        sort_by: 'created_at',
        sort_order: 'DESC'
      });

      // Add filters
      Object.entries(filter).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(`/api/orders/vendor/${vendorData.id}?${params}`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
        setPagination(data.pagination || pagination);
      } else {
        console.error('Failed to fetch orders:', response.status);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch order statistics
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const vendorData = JSON.parse(localStorage.getItem('vendorData') || '{}');
      
      const response = await fetch(`/api/orders/stats/${vendorData.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data.stats || {});
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch order details
  const fetchOrderDetails = async (orderId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/orders/${orderId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedOrder(data.order);
        setShowOrderDetails(true);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
    }
  };

  // Update order status
  const updateOrderStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/orders/${selectedOrder.id}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          reason: statusReason,
          notes: `Status updated to ${newStatus}`
        })
      });

      if (response.ok) {
        setShowStatusModal(false);
        setNewStatus('');
        setStatusReason('');
        fetchOrders(pagination.current_page);
        fetchStats();
        
        // Show success notification
        setNotifications(prev => [...prev, {
          id: Date.now(),
          type: 'success',
          message: `Order ${selectedOrder.order_number} status updated to ${newStatus}`,
          timestamp: new Date()
        }]);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to update order status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Error updating order status');
    }
  };

  // Export orders
  const exportOrders = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const vendorData = JSON.parse(localStorage.getItem('vendorData') || '{}');
      
      const params = new URLSearchParams({
        format,
        vendor_id: vendorData.id
      });

      // Add current filters
      Object.entries(filter).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });

      const response = await fetch(`/api/orders/export?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `orders-export-${Date.now()}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting orders:', error);
    }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const vendorData = JSON.parse(localStorage.getItem('vendorData') || '{}');
      
      const response = await fetch(`/api/orders/notifications/${vendorData.id}?limit=5&unread_only=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchStats();
    fetchNotifications();
  }, []);

  useEffect(() => {
    fetchOrders(1);
  }, [filter]);

  const handleFilterChange = (key, value) => {
    setFilter(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handlePageChange = (page) => {
    fetchOrders(page);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      'pending': 'status-pending',
      'confirmed': 'status-confirmed',
      'prepared': 'status-prepared',
      'ready': 'status-ready',
      'out_for_delivery': 'status-shipped',
      'delivered': 'status-delivered',
      'cancelled': 'status-cancelled',
      'rejected': 'status-rejected'
    };
    return statusClasses[status] || 'status-default';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock size={16} />;
      case 'confirmed': return <CheckCircle size={16} />;
      case 'prepared': return <Package size={16} />;
      case 'ready': return <CheckCircle2 size={16} />;
      case 'out_for_delivery': return <Truck size={16} />;
      case 'delivered': return <CheckCircle2 size={16} />;
      case 'cancelled': return <XCircle size={16} />;
      case 'rejected': return <X size={16} />;
      default: return <Clock size={16} />;
    }
  };

  const getStatusText = (status) => {
    const statusTexts = {
      'pending': 'Pending',
      'confirmed': 'Confirmed',
      'prepared': 'Prepared',
      'ready': 'Ready',
      'out_for_delivery': 'Out for Delivery',
      'delivered': 'Delivered',
      'cancelled': 'Cancelled',
      'rejected': 'Rejected'
    };
    return statusTexts[status] || status;
  };

  const getNextStatus = (currentStatus) => {
    const statusFlow = {
      'pending': 'confirmed',
      'confirmed': 'prepared',
      'prepared': 'ready',
      'ready': 'out_for_delivery',
      'out_for_delivery': 'delivered'
    };
    return statusFlow[currentStatus] || '';
  };

  const canUpdateStatus = (status) => {
    return ['pending', 'confirmed', 'prepared', 'ready', 'out_for_delivery'].includes(status);
  };

  const dismissNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  return (
    <div className="order-management">
      {/* Notifications */}
      {notifications.length > 0 && (
        <div className="notifications-container">
          {notifications.map(notification => (
            <div key={notification.id} className="notification">
              <Bell size={16} />
              <span>{notification.message || notification.title}</span>
              <button onClick={() => dismissNotification(notification.id)}>
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="order-management-header">
        <div className="header-content">
          <h1>Order Management</h1>
          <p>Manage and track all your orders</p>
        </div>
        <div className="header-actions">
          <div className="order-stats">
            <div className="stat-item">
              <span className="stat-number">{stats.total_orders || 0}</span>
              <span className="stat-label">Total Orders</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.pending_orders || 0}</span>
              <span className="stat-label">Pending</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{stats.delivered_orders || 0}</span>
              <span className="stat-label">Delivered</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">₹{stats.total_revenue || 0}</span>
              <span className="stat-label">Revenue</span>
            </div>
          </div>
          <button className="btn btn-secondary" onClick={() => fetchOrders(pagination.current_page)}>
            <RefreshCw size={20} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="order-filters">
        <div className="filters-left">
          <div className="search-container">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              placeholder="Search by order ID or customer name..."
              value={filter.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="search-input"
            />
          </div>
          
          <div className="filter-group">
            <Filter size={20} />
            <select
              value={filter.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="filter-select"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="prepared">Prepared</option>
              <option value="ready">Ready</option>
              <option value="out_for_delivery">Out for Delivery</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          <div className="filter-group">
            <CreditCard size={20} />
            <select
              value={filter.payment_status}
              onChange={(e) => handleFilterChange('payment_status', e.target.value)}
              className="filter-select"
            >
              <option value="">All Payments</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div className="date-filters">
            <input
              type="date"
              value={filter.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              className="date-input"
              placeholder="From Date"
            />
            <input
              type="date"
              value={filter.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              className="date-input"
              placeholder="To Date"
            />
          </div>
        </div>

        <div className="filters-right">
          <div className="results-count">
            {pagination.total_orders} orders
          </div>
          <div className="export-buttons">
            <button className="btn btn-secondary" onClick={() => exportOrders('csv')}>
              <Download size={16} />
              CSV
            </button>
            <button className="btn btn-secondary" onClick={() => exportOrders('pdf')}>
              <Download size={16} />
              PDF
            </button>
          </div>
        </div>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="loading">Loading orders...</div>
      ) : (
        <>
          <div className="orders-list">
            {orders.map((order) => (
              <div key={order.id} className="order-card">
                <div className="order-header">
                  <div className="order-info">
                    <h3 className="order-number">{order.order_number}</h3>
                    <p className="order-date">
                      <Calendar size={14} />
                      {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="order-status">
                    <span className={`status-badge ${getStatusBadge(order.status)}`}>
                      {getStatusIcon(order.status)}
                      {getStatusText(order.status)}
                    </span>
                    <span className={`payment-badge payment-${order.payment_status}`}>
                      <CreditCard size={14} />
                      {order.payment_status?.toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="order-details">
                  <div className="customer-info">
                    <div className="info-item">
                      <User size={16} />
                      <span>{order.customer_name}</span>
                    </div>
                    <div className="info-item">
                      <MapPin size={16} />
                      <span>{order.shipping_address}</span>
                    </div>
                  </div>

                  <div className="order-summary">
                    <div className="summary-item">
                      <span className="label">Products:</span>
                      <span className="value">{order.products}</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Total:</span>
                      <span className="value">₹{order.total_amount}</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">Payment:</span>
                      <span className="value">{order.payment_mode}</span>
                    </div>
                  </div>
                </div>

                <div className="order-actions">
                  <button 
                    className="btn btn-secondary btn-sm"
                    onClick={() => fetchOrderDetails(order.id)}
                  >
                    <Eye size={16} />
                    View Details
                  </button>
                  
                  {canUpdateStatus(order.status) && (
                    <button 
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        setSelectedOrder(order);
                        setNewStatus(getNextStatus(order.status));
                        setShowStatusModal(true);
                      }}
                    >
                      <ArrowRight size={16} />
                      Update Status
                    </button>
                  )}

                  {order.status === 'pending' && (
                    <button 
                      className="btn btn-danger btn-sm"
                      onClick={() => {
                        setSelectedOrder(order);
                        setNewStatus('cancelled');
                        setStatusReason('Cancelled by vendor');
                        setShowStatusModal(true);
                      }}
                    >
                      <XCircle size={16} />
                      Cancel
                    </button>
                  )}
                </div>
              </div>
            ))}

            {orders.length === 0 && (
              <div className="empty-state">
                <ShoppingCart size={48} />
                <h3>No orders found</h3>
                <p>No orders match your current filters</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="pagination">
              <button 
                className="btn btn-secondary"
                disabled={!pagination.has_prev}
                onClick={() => handlePageChange(pagination.current_page - 1)}
              >
                Previous
              </button>
              
              <span className="pagination-info">
                Page {pagination.current_page} of {pagination.total_pages}
              </span>
              
              <button 
                className="btn btn-secondary"
                disabled={!pagination.has_next}
                onClick={() => handlePageChange(pagination.current_page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* Order Details Modal */}
      {showOrderDetails && selectedOrder && (
        <div className="modal-overlay">
          <div className="modal large">
            <div className="modal-header">
              <h2>Order Details - {selectedOrder.order_number}</h2>
              <button className="close-btn" onClick={() => setShowOrderDetails(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="order-details-grid">
                <div className="details-section">
                  <h3>Customer Information</h3>
                  <div className="details-content">
                    <div className="detail-item">
                      <span className="label">Name:</span>
                      <span className="value">{selectedOrder.customer_name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Email:</span>
                      <span className="value">{selectedOrder.customer_email || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Phone:</span>
                      <span className="value">{selectedOrder.customer_phone}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Address:</span>
                      <span className="value">{selectedOrder.shipping_address}</span>
                    </div>
                  </div>
                </div>

                <div className="details-section">
                  <h3>Order Information</h3>
                  <div className="details-content">
                    <div className="detail-item">
                      <span className="label">Status:</span>
                      <span className={`status-badge ${getStatusBadge(selectedOrder.status)}`}>
                        {getStatusIcon(selectedOrder.status)}
                        {getStatusText(selectedOrder.status)}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Payment Status:</span>
                      <span className={`payment-badge payment-${selectedOrder.payment_status}`}>
                        {selectedOrder.payment_status?.toUpperCase()}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Payment Mode:</span>
                      <span className="value">{selectedOrder.payment_mode}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Transaction ID:</span>
                      <span className="value">{selectedOrder.transaction_id || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              {selectedOrder.items && selectedOrder.items.length > 0 && (
                <div className="details-section">
                  <h3>Order Items</h3>
                  <div className="items-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>SKU</th>
                          <th>Quantity</th>
                          <th>Unit Price</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.items.map((item, index) => (
                          <tr key={index}>
                            <td>{item.product_name}</td>
                            <td>{item.product_sku}</td>
                            <td>{item.quantity}</td>
                            <td>₹{item.unit_price}</td>
                            <td>₹{item.total_price}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Order Summary */}
              <div className="details-section">
                <h3>Order Summary</h3>
                <div className="order-summary-details">
                  <div className="summary-row">
                    <span>Subtotal:</span>
                    <span>₹{selectedOrder.subtotal}</span>
                  </div>
                  <div className="summary-row">
                    <span>Tax:</span>
                    <span>₹{selectedOrder.tax_amount}</span>
                  </div>
                  <div className="summary-row">
                    <span>Shipping:</span>
                    <span>₹{selectedOrder.shipping_charges}</span>
                  </div>
                  <div className="summary-row total">
                    <span>Total:</span>
                    <span>₹{selectedOrder.total_amount}</span>
                  </div>
                </div>
              </div>

              {/* Status Timeline */}
              {selectedOrder.status_history && selectedOrder.status_history.length > 0 && (
                <div className="details-section">
                  <h3>Status Timeline</h3>
                  <div className="status-timeline">
                    {selectedOrder.status_history.map((history, index) => (
                      <div key={index} className="timeline-item">
                        <div className="timeline-icon">
                          {getStatusIcon(history.new_status)}
                        </div>
                        <div className="timeline-content">
                          <div className="timeline-title">
                            {getStatusText(history.new_status)}
                          </div>
                          <div className="timeline-meta">
                            <span>{new Date(history.created_at).toLocaleString()}</span>
                            <span>by {history.changed_by}</span>
                          </div>
                          {history.notes && (
                            <div className="timeline-notes">{history.notes}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowOrderDetails(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && selectedOrder && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Update Order Status</h2>
              <button className="close-btn" onClick={() => setShowStatusModal(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Order: <strong>{selectedOrder.order_number}</strong></p>
              <p>Current Status: <strong>{getStatusText(selectedOrder.status)}</strong></p>
              <p>New Status: <strong>{getStatusText(newStatus)}</strong></p>
              
              {newStatus === 'cancelled' && (
                <div className="form-group">
                  <label>Cancellation Reason:</label>
                  <textarea
                    value={statusReason}
                    onChange={(e) => setStatusReason(e.target.value)}
                    placeholder="Enter reason for cancellation..."
                    rows="3"
                  />
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowStatusModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={updateOrderStatus}
              >
                Update Status
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagement;
